import { createRoot } from 'react-dom/client';
import { matchPath } from 'react-router-dom';

import './trusted-security-policies';
import './extensions';
import 'simplebar/src/simplebar.css';
import 'react-modern-drawer/dist/index.css';
// kula's stylesheet before the app's own: both ship plain Tailwind utilities at
// equal specificity, so whichever loads last wins a tie. With kula last, a
// utility passed to one of its components (`triggerClassName="pl-4"` over the
// variant's own `px-3`) silently lost, and the fix looked like it had to be
// `!important` or a second copy of kula's classes in our Tailwind build.
import '@kula-ai/components/style.css';
import './styles/index.css';
import { createBrowserHistory } from 'history';

import { App as Core } from './app';
import './utils/yup';

// integrations
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import { PostHogProvider } from 'posthog-js/react';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';
import { SENTRY_ROUTE_PATTERNS } from '@/constants/routePatterns';
import { installDomMutationGuards } from '@/lib/domMutationGuards';
import { whenIdle } from '@/utils/whenIdle';
import { posthogOptionsForPath } from '@/utils/analyticsGating';
import { AssessmentApp } from './assessment/app';
import { IntegrationApp } from 'integrations/app';
import { CandidateSurveyApp } from './candidateSurvey/app';
import { CandidateConsentApp } from './candidateConsent/app';

if (import.meta.env.VITE_SENTRY_DSN) {
  const history = createBrowserHistory();

  const EXTENSION_URL_RE =
    /^(chrome-extension|moz-extension|safari-extension|safari-web-extension):\/\//;

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENV,
    // `sentryVitePlugin` already uploads sourcemaps under this release name
    // (vite.config.ts), and all five deploy workflows export VITE_RELEASE — but
    // without it here the events carry no release, so Sentry has nothing to
    // match those artifacts against and stack traces stay minified. Setting it
    // also makes per-release comparison possible, which is what lets a
    // performance change be attributed to the deploy that shipped it rather
    // than to whatever else happened to land that week.
    release: import.meta.env.VITE_RELEASE,
    integrations: [
      new Sentry.Replay(),
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV5Instrumentation(
          history,
          SENTRY_ROUTE_PATTERNS,
          matchPath
        ),
        shouldCreateSpanForRequest: (url) =>
          !url.includes('kula-ai-attachments-prod.s3.amazonaws.com/resumes'),
      }),
    ],

    // Unattended session replays are off: nobody watches them, and at 0.1 they
    // were the bulk of the org's replay spend. Once that quota is exhausted
    // Sentry drops EVERY replay — including the error ones below — so recording
    // sessions nobody opens was crowding out the ones worth having.
    // `beforeSend` cannot help here; it only runs for error events.
    replaysSessionSampleRate: 0,
    // Unchanged: a replay attached to a real error is the one worth keeping.
    replaysOnErrorSampleRate: 1.0,
    // Raised from 0.1 while establishing a per-route performance baseline: the
    // route table above was only covering 32 of 58 pages, so the busy routes
    // have usable data but the quieter ones are too thin to read. Expect ~3x the
    // transaction volume against the Sentry quota — drop back to 0.1 once a
    // couple of weeks of data has accumulated.
    tracesSampleRate: 0.3,
    ignoreErrors: [
      /^ChunkLoadError$/,
      '_avast_submit',
      /RenderingCancelledException/,
      /UnknownErrorException.*ensureDoc/,
      /Transport destroyed/,
      /AbortError.*play\(\)/,
      /^LaunchDarklyFlagFetchError/,
      /bufferBelongsToIframe/,
      /ResizeObserver loop/,
      /Non-Error promise rejection captured.*Object Not Found Matching Id/,
    ],
    denyUrls: [EXTENSION_URL_RE],
    beforeSend(event, hint) {
      const error = hint?.originalException as
        | (Error & {
            name?: string;
            message?: string;
            response?: { status?: number };
          })
        | undefined;

      if (error?.name === 'AbortError' || error?.name === 'CanceledError') {
        return null;
      }

      const message = error?.message || event.message || '';
      if (
        /Network Error|Failed to fetch|Load failed|network error/i.test(message)
      ) {
        return null;
      }

      const status = error?.response?.status;
      if (status && status >= 500 && status <= 599) {
        return null;
      }

      const topFrame =
        event.exception?.values?.[0]?.stacktrace?.frames?.slice(-1)[0];
      const topFile = topFrame?.filename || '';
      if (EXTENSION_URL_RE.test(topFile) || topFile.startsWith('blob:')) {
        return null;
      }

      return event;
    },
  });
}

// Before the first render, after `Sentry.init` so recoveries get reported: a
// node rewritten outside React (page translation, an extension) otherwise
// throws mid-commit and takes the whole page down. See `domMutationGuards`.
installDomMutationGuards();

const container = document.getElementById('root');
const root = createRoot(container);

const AppToRender = () => {
  const isAssessment = window?.location?.host?.split('.')?.[0] === 'assessment';
  const subdomain = window?.location?.host?.split('.')?.[0];
  //https://integration.kula.ai/rippling
  const isIntegration = subdomain === 'integration';
  const isCandidateSurvey = subdomain === 'survey';
  const isConsent = subdomain === 'consent';

  if (isAssessment) {
    return <AssessmentApp />;
  }
  if (isIntegration) {
    return <IntegrationApp />;
  }
  if (isCandidateSurvey) {
    // TODO: Uncomment this once the candidate survey app is ready
    return <CandidateSurveyApp />;
  }
  if (isConsent) {
    return <CandidateConsentApp />;
  }
  return <Core />;
};

export const AppWithLD = withLDProvider({
  clientSideID: import.meta.env.VITE_LAUNCHDARKLY,
  context: {
    kind: 'user',
    key: `${window.location.host.split('.')[0]}:anonymous`,
  },
  options: {
    streaming: false,
    // SDK health telemetry posted back to LaunchDarkly about LaunchDarkly — it
    // carries none of our data and nothing reads it on our side. Sentry measures
    // `events/diagnostic` at p75 1,136ms on /tasks/jobs/approvals and 1,448ms on
    // /settings/home, which is a lot of page-load budget for a vendor's own
    // metrics. `sendEvents` is deliberately left alone: that one carries flag
    // evaluations the team may actually be reading.
    diagnosticOptOut: true,
  },
})(AppToRender);

// posthog config
if (import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
  // `init` is what pulls PostHog's six separate bundles — recorder, surveys,
  // web-vitals, dead-clicks, exception-autocapture and remote config — and
  // Sentry measures those at roughly 2.4s of script time per page load. Running
  // it at module scope put all of that in front of `root.render` below, which
  // is a large part of why LCP sits at p75 3.5s while FCP is 169ms.
  //
  // Deferring to idle keeps every feature: `init` still fires the initial
  // $pageview, just after the app has painted. The trade is that a `capture`
  // call made in the first moments is a no-op, so the `timeout` is kept short.
  whenIdle(() => {
    posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      // Read once, at init: the SPA never re-inits on navigation, so a visitor
      // who signs in keeps this session's settings and picks the add-ons up on
      // their next load. See `posthogOptionsForPath` for the measurements.
      ...posthogOptionsForPath(window.location.pathname),
      loaded: function (ph) {
        // setting posthog on window for accessing posthog toolbar
        Object.assign(window, { posthog: ph });
      },
    });
  });
}

export const App = () => {
  if (import.meta.env.VITE_LAUNCHDARKLY) {
    return <AppWithLD />;
  }
  return (
    <PostHogProvider client={posthog}>
      <AppToRender />
    </PostHogProvider>
  );
};

root.render(<App />);
